/**
 * v2.7.65+E126 / E127 · Driver Console · Lyft-inspired mobile UI
 * ═════════════════════════════════════════════════════════════════════
 * Full-screen dark driver surface at `/drive`. Preserves the existing
 * meter engine (accessed via bottom-sheet CTA) and layers Lyft-style
 * chrome on top. E127 additions:
 *
 *   • Wait-times sheet pulls real dispatch queue metrics
 *   • Klaxon auto-open — booking event slides the meter sheet open
 *   • Safety Hub setup flows (Emergency contacts / PIN / Record audio)
 *   • Turbo card only lights up when driver is in a real promo zone
 *   • Custom taxi-yellow / ruby theme applied via CSS variables
 *
 * Twilio Lock (per ops directive):
 *   · This page NEVER initiates a Twilio SMS / voice call. All action
 *     tiles that would call out are dry-run until Derick approves.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Menu, Bell, Power, Loader2, MapPin, Flag, User2, Car,
  Sparkles, ChevronRight, Rocket, Award, Home, Gauge,
  AlertTriangle, ClockPlus, Layers, LocateFixed, X, Siren,
  PhoneCall, Mic, Users, KeyRound, VideoIcon, ShieldPlus,
  Ban, Coffee, Fuel, Landmark, CalendarDays, TrendingUp,
  Star, ChevronDown, RefreshCw, Trash2, Copy, Circle,
  Gift, Check, User,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtUsd } from "@/lib/format";
import NavigationActionSheet from "@/components/driver/NavigationActionSheet";
import DriverPreferencesCard from "@/components/driver/DriverPreferencesCard";
import { CustomerTypeBadge } from "@/components/CustomerTypeBadge";

const DISPATCH_TEL = "+15802194687";
const POLL_MS_ACTIVE = 15_000;
const POLL_MS_IDLE   = 60_000;
const WAIT_TIMES_POLL_MS = 45_000;
const TURBO_POLL_MS = 60_000;

// ─── v2.7.65+E127 Theme ──────────────────────────────────────────────
// Custom taxi-yellow / ruby palette applied via CSS variables so we
// don't have to edit tailwind.config. Reference inside JSX with
// `style={{ color: "var(--dc-primary)" }}`.
const THEME = {
  "--dc-primary":      "#FFD200",   // Taxi yellow
  "--dc-secondary":    "#E0115F",   // Ruby
  "--dc-danger":       "#D32F2F",
  "--dc-neutral-dark": "#212529",
  "--dc-neutral-light":"#F8F9FA",
  "--dc-surface":      "#343A40",
};

const REPORT_TILES = [
  { key: "traffic",  label: "Traffic",  Icon: Car,           tone: "ruby"   },
  { key: "hazard",   label: "Hazard",   Icon: AlertTriangle, tone: "yellow" },
  { key: "police",   label: "Police",   Icon: ShieldPlus,    tone: "sky"    },
  { key: "accident", label: "Accident", Icon: Siren,         tone: "red"    },
  { key: "closure",  label: "Closure",  Icon: X,             tone: "ruby"   },
  { key: "other",    label: "Other",    Icon: Sparkles,      tone: "zinc"   },
];

const EARN_MENU = [
  { key: "wait-times",   label: "Wait times",       hint: "See ride-request wait times per zone",  Icon: ClockPlus, kind: "wait-times" },
  { key: "demand",       label: "Recent ride demand", hint: "Find areas with the most pickups", Icon: TrendingUp, kind: "toast" },
  { key: "nearby",       label: "Nearby drivers",   hint: "See other drivers waiting for rides", Icon: Users, kind: "toast" },
  { key: "scheduled",    label: "Scheduled rides",  hint: "Reserve upcoming rides",       Icon: CalendarDays, kind: "toast" },
];
const REFRESH_MENU = [
  { key: "gas",        label: "Gas savings",        hint: "Find gas stations offering cash back", Icon: Fuel },
  { key: "restrooms",  label: "Restrooms",          hint: "See restrooms near you",        Icon: Coffee },
  { key: "atms",       label: "ATMs",               hint: "See ATMs with multiple withdrawal types", Icon: Landmark },
];

const SAFETY_ITEMS = [
  { key: "record-audio",       label: "Record audio",           hint: "Capture encrypted audio of rides",     Icon: Mic,       kind: "audio" },
  { key: "share-loc",          label: "Share your location",    hint: "Let trusted contacts see your ride",   Icon: MapPin,    kind: "toggle-share" },
  { key: "pin-verify",         label: "Verify rides with a PIN",hint: "Confirm you're with the right passenger", Icon: KeyRound, kind: "pin" },
  { key: "adt-video",          label: "Allow ADT video support",hint: "Let ADT record in a future emergency", Icon: VideoIcon,  kind: "coming-soon" },
  { key: "emergency-contacts", label: "Add emergency contacts", hint: "Choose who Buller Taxi should contact", Icon: PhoneCall, kind: "contacts" },
  { key: "block",              label: "Block a passenger",      hint: "Avoid matching in the future",         Icon: Ban,       kind: "coming-soon" },
];

// ─── Distance helper (equirectangular) ────────────────────────────
function distMiles(a, b) {
  if (!a || !b) return Infinity;
  const R = 3958.8; // Earth radius miles
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export default function DriverConsolePage() {
  const nav = useNavigate();

  const [me, setMe] = useState(null);
  const [online, setOnline] = useState(false);
  const [toggling, setToggling] = useState(false);

  const [activeRide, setActiveRide] = useState(null);
  const [ridePolling, setRidePolling] = useState(false);
  const pollTimer = useRef(null);

  // v2.8 · Redeem-at-Pickup free-ride PIN (loyalty)
  const [freeRidePin, setFreeRidePin] = useState("");
  const [freeRideBusy, setFreeRideBusy] = useState(false);
  const [freeRideDone, setFreeRideDone] = useState(null);   // { rider_name } after redeem
  const [freeRideOwner, setFreeRideOwner] = useState(null); // { found, rider_name } preview
  const redeemFreeRidePin = useCallback(async () => {
    const code = (freeRidePin || "").replace(/\D/g, "");
    if (code.length !== 6) { toast.error("Enter the rider's 6-digit free-ride code."); return; }
    setFreeRideBusy(true);
    try {
      const { data } = await api.post("/driver/loyalty/verify-pin", { pin: code, ride_id: activeRide?.ride_id });
      setFreeRideDone({ rider_name: data.rider_name });
      setFreeRidePin("");
      toast.success(`Free ride redeemed for ${data.rider_name} · comp the fare`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or already-redeemed code");
    } finally { setFreeRideBusy(false); }
  }, [freeRidePin, activeRide]);

  useEffect(() => {
    if (freeRideDone) return;
    if (freeRidePin.length !== 6) { setFreeRideOwner(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/driver/loyalty/pin-owner", { params: { pin: freeRidePin } });
        if (!cancelled) setFreeRideOwner(data);
      } catch { if (!cancelled) setFreeRideOwner({ found: false }); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [freeRidePin, freeRideDone]);

  useEffect(() => {
    // Clear the free-ride redemption UI when the active ride changes.
    setFreeRidePin(""); setFreeRideDone(null); setFreeRideOwner(null);
  }, [activeRide?.ride_id]);

  // v2.8 · One-tap "Ride as myself" — hop into the rider app with the driver's
  // linked rider profile, no password re-entry.
  const [riderModeBusy, setRiderModeBusy] = useState(false);
  const openRiderMode = useCallback(async () => {
    setRiderModeBusy(true);
    try {
      const { data } = await api.post("/driver/rider-mode");
      localStorage.setItem("rtg_customer_token", data.access_token);
      localStorage.setItem("rtg_customer", JSON.stringify(data.customer));
      window.location.href = "/ride";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open rider mode");
      setRiderModeBusy(false);
    }
  }, []);


  const [earnings, setEarnings] = useState(null);

  // Sheet toggles
  const [meterOpen, setMeterOpen]         = useState(false);
  const [reportOpen, setReportOpen]       = useState(false);
  const [earnOpen, setEarnOpen]           = useState(false);
  const [safetyOpen, setSafetyOpen]       = useState(false);
  const [profileOpen, setProfileOpen]     = useState(false);
  const [waitTimesOpen, setWaitTimesOpen] = useState(false);
  const [contactsOpen, setContactsOpen]   = useState(false);
  const [pinOpen, setPinOpen]             = useState(false);
  const [audioOpen, setAudioOpen]         = useState(false);
  const [safetyTab, setSafetyTab]         = useState("tools");

  const [layerMode, setLayerMode] = useState("streets");
  const [locating, setLocating]   = useState(false);

  // ─── v2.7.65+E127 · Wait-times ──────────────────────────────────
  const [waitTimes, setWaitTimes] = useState(null);
  const [wtLoading, setWtLoading] = useState(false);

  // ─── v2.7.65+E127 · Safety state ────────────────────────────────
  const [safety, setSafety] = useState({ emergency_contacts: [], safety_settings: {} });
  const [newContact, setNewContact] = useState({ name: "", phone: "", relationship: "" });
  const [addingContact, setAddingContact] = useState(false);

  // ─── v2.7.65+E127 · PIN verify (client-only for MVP) ────────────
  const [ridePin, setRidePin] = useState(null);

  // ─── v2.7.65+E127 · Audio recording (MediaRecorder, local blob) ─
  const [recording, setRecording] = useState(false);
  const [recorder, setRecorder]   = useState(null);
  const [chunks, setChunks]       = useState([]);
  const [lastRecordingUrl, setLastRecordingUrl] = useState(null);

  // ─── v2.7.65+E127 · Live geo + turbo zone ───────────────────────
  const [geo, setGeo]         = useState(null);            // {lat, lng}
  const [currentZone, setCurrentZone] = useState(null);    // matched zone or null

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
        setOnline(!!data.online);
      } catch { /* silent */ }
    })();
  }, []);

  // Poll active-ride
  const pollActiveRide = useCallback(async () => {
    setRidePolling(true);
    try {
      const { data } = await api.get("/driver/active-ride");
      setActiveRide(data?.has_active_ride ? data : null);
    } catch { setActiveRide(null); }
    finally { setRidePolling(false); }
  }, []);

  // v2.8.2 · Close/dismiss a stuck or unresolvable trip without recording it
  // as a completed trip (does not touch the Completed Rides ledger).
  const dismissStuckRide = useCallback(async () => {
    if (!activeRide?.ride_id) return;
    if (!window.confirm("Close this trip and remove it from your app?\n\nUse this only for a stuck, duplicate, or old trip. It will NOT be recorded as a completed trip.")) return;
    try {
      await api.post(`/rides/${activeRide.ride_id}/driver-dismiss`);
      toast.success("Trip closed & cleared from your app");
      setActiveRide(null);
      pollActiveRide();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not close trip"); }
  }, [activeRide, pollActiveRide]);

  useEffect(() => {
    pollActiveRide();
    const schedule = () => {
      const interval = activeRide ? POLL_MS_ACTIVE : POLL_MS_IDLE;
      pollTimer.current = setTimeout(async () => {
        await pollActiveRide();
        schedule();
      }, interval);
    };
    schedule();
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, [activeRide?.ride_id, pollActiveRide]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/driver/earnings");
        setEarnings(data);
      } catch { /* silent */ }
    })();
  }, [activeRide?.ride_id]);

  // ─── v2.7.65+E127 · Wait-times auto-fetch when sheet opens ─────
  const fetchWaitTimes = useCallback(async () => {
    setWtLoading(true);
    try {
      const { data } = await api.get("/driver/wait-times");
      setWaitTimes(data);
    } catch { /* silent */ }
    finally { setWtLoading(false); }
  }, []);

  useEffect(() => {
    if (!waitTimesOpen) return undefined;
    fetchWaitTimes();
    const t = setInterval(fetchWaitTimes, WAIT_TIMES_POLL_MS);
    return () => clearInterval(t);
  }, [waitTimesOpen, fetchWaitTimes]);

  // ─── v2.7.65+E127 · Safety data load on mount ──────────────────
  const loadSafety = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/safety");
      setSafety({
        emergency_contacts: data.emergency_contacts || [],
        safety_settings:    data.safety_settings || {},
      });
    } catch { /* silent — safety hub degrades gracefully */ }
  }, []);
  useEffect(() => { loadSafety(); }, [loadSafety]);

  // ─── v2.7.65+E127 · Klaxon Auto-Open ───────────────────────────
  // When the global audio engine fires rtg:public_booking (new online
  // booking) OR rtg:trip_initiated (accepted), auto-slide the meter
  // sheet so the trip lands one tap away — no hunting through the UI.
  useEffect(() => {
    const onIncoming = () => {
      setMeterOpen(true);
      toast.success("New booking incoming — meter opened");
    };
    const onInitiated = () => setMeterOpen(true);
    window.addEventListener("rtg:public_booking",  onIncoming);
    window.addEventListener("rtg:trip_initiated",  onInitiated);
    return () => {
      window.removeEventListener("rtg:public_booking",  onIncoming);
      window.removeEventListener("rtg:trip_initiated",  onInitiated);
    };
  }, []);

  // ─── v2.7.65+E127 · Geolocation + Turbo-zone detection ─────────
  const watchIdRef = useRef(null);
  useEffect(() => {
    if (!("geolocation" in navigator)) return undefined;
    const onPos = (pos) => {
      const g = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setGeo(g);
    };
    const onErr = () => { /* silent — driver may have denied permission */ };
    watchIdRef.current = navigator.geolocation.watchPosition(onPos, onErr, {
      enableHighAccuracy: false,
      maximumAge: 60_000,
      timeout: 15_000,
    });
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  // Recompute current zone whenever geo or waitTimes update (waitTimes
  // brings the zone list). Also poll wait-times in the background so
  // the Turbo card stays fresh without opening the sheet.
  useEffect(() => {
    if (!geo) return undefined;
    // Kick a lightweight fetch on-demand so we know the zones.
    fetchWaitTimes();
    const t = setInterval(fetchWaitTimes, TURBO_POLL_MS);
    return () => clearInterval(t);
  }, [geo?.lat, geo?.lng, fetchWaitTimes]);

  useEffect(() => {
    if (!geo || !waitTimes?.zones?.length) { setCurrentZone(null); return; }
    let best = null;
    let bestD = Infinity;
    for (const z of waitTimes.zones) {
      const d = distMiles(geo, z.center);
      if (d <= (z.radius_mi || 3.5) && d < bestD) { best = z; bestD = d; }
    }
    setCurrentZone(best);
  }, [geo?.lat, geo?.lng, waitTimes?.zones]);

  const toggleOnline = async () => {
    setToggling(true);
    try {
      const next = !online;
      await api.post("/drivers/online", { online: next });
      setOnline(next);
      if (next) toast.success("You're online — dispatch can send you rides");
      else       toast.message("You're offline");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    } finally { setToggling(false); }
  };

  const submitReport = (key, label) => {
    console.info("[driver-report]", { kind: key, label, mock: true, ts: new Date().toISOString() });
    toast.success(`${label} report logged (dry-run — no dispatch call fired)`);
    setReportOpen(false);
  };

  // ─── Safety Hub · Emergency Contacts ────────────────────────────
  const addContact = async () => {
    setAddingContact(true);
    try {
      const { data } = await api.post("/driver/safety/emergency-contacts", newContact);
      setSafety((s) => ({ ...s, emergency_contacts: [...s.emergency_contacts, data] }));
      setNewContact({ name: "", phone: "", relationship: "" });
      toast.success(`${data.name} added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add contact");
    } finally { setAddingContact(false); }
  };
  const removeContact = async (id) => {
    try {
      await api.delete(`/driver/safety/emergency-contacts/${id}`);
      setSafety((s) => ({
        ...s,
        emergency_contacts: s.emergency_contacts.filter((c) => c.id !== id),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove contact");
    }
  };

  // ─── Safety Hub · PIN verify (client-only, per-ride) ────────────
  const rollPin = useCallback(() => {
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    setRidePin(pin);
    return pin;
  }, []);
  useEffect(() => {
    // Generate a new PIN each time a fresh active ride starts.
    if (activeRide?.ride_id && !ridePin) rollPin();
    if (!activeRide?.ride_id) setRidePin(null);
  }, [activeRide?.ride_id, ridePin, rollPin]);

  const copyPin = async () => {
    if (!ridePin) return;
    try { await navigator.clipboard.writeText(ridePin); toast.success("PIN copied"); }
    catch { toast.message(`PIN: ${ridePin}`); }
  };

  // ─── Safety Hub · Audio recording (MediaRecorder → local blob) ─
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const localChunks = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) localChunks.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(localChunks, { type: "audio/webm" });
        setLastRecordingUrl(URL.createObjectURL(blob));
        setChunks([]);
        toast.success("Recording saved locally — not uploaded per Twilio-lock");
      };
      mr.start(1000);
      setRecorder(mr);
      setChunks(localChunks);
      setRecording(true);
      toast.message("Recording on device — audio never leaves your phone");
    } catch (e) {
      toast.error(e.message || "Microphone permission denied");
    }
  };
  const stopRecording = () => {
    try { recorder?.stop(); } catch { /* noop */ }
    setRecording(false);
    setRecorder(null);
  };

  const toggleSafetySetting = async (key, value) => {
    try {
      const { data } = await api.patch("/driver/safety/settings", { [key]: value });
      setSafety({
        emergency_contacts: data.emergency_contacts || [],
        safety_settings:    data.safety_settings || {},
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save setting");
    }
  };

  const handleSafetyItem = (item) => {
    switch (item.kind) {
      case "contacts":     setContactsOpen(true); break;
      case "pin":          setPinOpen(true); break;
      case "audio":        setAudioOpen(true); break;
      case "toggle-share":
        toggleSafetySetting("share_location", !safety.safety_settings?.share_location);
        break;
      case "coming-soon":
      default:             toast.message(`${item.label} · coming soon`);
    }
  };

  const handleEarnItem = (item) => {
    if (item.kind === "wait-times") {
      setEarnOpen(false);
      setWaitTimesOpen(true);
      return;
    }
    setEarnOpen(false);
    toast.message(`${item.label} · coming soon`);
  };

  const pickup = useMemo(() => activeRide ? {
    lat: activeRide.pickup_coordinates?.lat,
    lng: activeRide.pickup_coordinates?.lng,
    address: activeRide.pickup_address,
  } : null, [activeRide]);
  const destination = useMemo(() => activeRide ? {
    lat: activeRide.destination_coordinates?.lat,
    lng: activeRide.destination_coordinates?.lng,
    address: activeRide.destination_address,
  } : null, [activeRide]);

  const shiftTotal = fmtUsd(earnings?.today_total || 0);
  const notifCount = 1;

  return (
    <div
      className="min-h-screen text-white relative overflow-hidden"
      style={{ background: "#141518", ...THEME }}
      data-testid="driver-console"
    >
      {/* ── FULL-BLEED MAP SURFACE ────────────────────────────────────── */}
      <div className="absolute inset-0 bg-zinc-950" data-testid="map-surface">
        <div className={`absolute inset-0 transition-opacity ${layerMode === "streets" ? "opacity-100" : "opacity-40"}`}
             style={{
               backgroundImage: `
                 radial-gradient(circle at 30% 40%, rgba(224,17,95,0.20) 0%, transparent 55%),
                 radial-gradient(circle at 75% 60%, rgba(255,210,0,0.10) 0%, transparent 45%),
                 linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                 linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
               `,
               backgroundSize: "100% 100%, 100% 100%, 40px 40px, 40px 40px",
             }}
        />
        <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 400 800" preserveAspectRatio="xMidYMid slice">
          <path d="M-20 300 Q 100 260 200 310 T 420 260" stroke="#6b7280" strokeWidth="14" fill="none"/>
          <path d="M180 -20 Q 200 200 210 400 T 240 820" stroke="#6b7280" strokeWidth="10" fill="none"/>
          <path d="M-20 500 Q 150 450 260 500 T 420 460" stroke="#4b5563" strokeWidth="8" fill="none"/>
        </svg>
        <div className="absolute top-[30%] left-[12%] text-[9px] font-black bg-white text-black rounded-full h-6 w-6 grid place-items-center shadow">66</div>
        <div className="absolute top-[42%] left-[45%] text-[9px] font-black bg-blue-600 text-white rounded-t-full h-7 w-6 grid place-items-center shadow">344</div>
        <div className="absolute top-[62%] right-[15%] text-[9px] font-black bg-red-600 text-white rounded h-6 w-8 grid place-items-center shadow">I-40</div>

        <div
          className="absolute top-[46%] left-1/2 -translate-x-1/2 h-14 w-14 rounded-full border-4 bg-white grid place-items-center shadow-2xl"
          style={{ borderColor: "var(--dc-primary)" }}
          data-testid="driver-position-arrow"
        >
          <div className="h-0 w-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[16px] border-b-slate-900"/>
        </div>
      </div>

      {/* ── TOP-LEFT MENU PILL ────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setProfileOpen(true)}
        data-testid="header-menu-btn"
        className="absolute top-3 left-3 z-30 h-11 w-11 rounded-2xl bg-zinc-900/85 backdrop-blur-xl border border-white/10 grid place-items-center shadow-xl hover:bg-zinc-800/85 active:scale-95 transition-all"
        aria-label="Open profile menu"
      >
        <Menu className="h-5 w-5 text-white"/>
      </button>

      {/* ── TOP-CENTRE EARNINGS PILL ─────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setEarnOpen(true)}
        data-testid="header-earnings-pill"
        className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-4 h-11 rounded-full bg-zinc-900/90 backdrop-blur-xl border border-white/10 shadow-xl hover:bg-zinc-800/90 active:scale-95 transition-all"
        aria-label="Open earnings dashboard"
      >
        <span className="text-lg font-black tracking-tight text-white">{shiftTotal}</span>
        {activeRide && (
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-400 border-l border-white/20 pl-2">On Trip</span>
        )}
      </button>

      {/* ── TOP-RIGHT BELL ───────────────────────────────────────────── */}
      <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
        <button
          type="button"
          onClick={() => nav("/dashboard")}
          data-testid="header-notifications-btn"
          className="relative h-11 w-11 rounded-2xl bg-zinc-900/85 backdrop-blur-xl border border-white/10 grid place-items-center shadow-xl hover:bg-zinc-800/85 active:scale-95 transition-all"
          aria-label="Notifications & classic dashboard"
        >
          <Bell className="h-5 w-5 text-white"/>
          {notifCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full grid place-items-center text-[9px] font-black text-white"
              style={{ background: "var(--dc-secondary)" }}
            >
              {notifCount}
            </span>
          )}
        </button>
      </div>

      {/* ── ONLINE PILL ──────────────────────────────────────────────── */}
      {!online && (
        <button
          type="button"
          onClick={toggleOnline}
          disabled={toggling}
          data-testid="driver-online-toggle"
          className="absolute top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-4 h-9 rounded-full active:scale-95 transition-all shadow-xl"
          style={{ background: "var(--dc-primary)" }}
          aria-pressed={online}
        >
          {toggling
            ? <Loader2 className="h-3.5 w-3.5 animate-spin text-black"/>
            : <Power className="h-3.5 w-3.5 text-black"/>}
          <span className="text-[11px] uppercase tracking-widest font-black text-black">
            Go Online
          </span>
        </button>
      )}
      {online && (
        <button
          type="button"
          onClick={toggleOnline}
          disabled={toggling}
          data-testid="driver-online-toggle"
          className="absolute top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 h-8 rounded-full border backdrop-blur-xl active:scale-95 transition-all"
          style={{ borderColor: "var(--dc-primary)", background: "rgba(255,210,0,0.15)" }}
          aria-pressed={online}
        >
          {toggling
            ? <Loader2 className="h-3 w-3 animate-spin" style={{ color: "var(--dc-primary)" }}/>
            : <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: "var(--dc-primary)" }}/>}
          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--dc-primary)" }}>Online</span>
        </button>
      )}

      {/* ── RIGHT-EDGE ACTION STACK ─────────────────────────────────── */}
      <div className="absolute top-[38%] right-3 z-30 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          data-testid="action-stack-report"
          className="h-11 w-11 rounded-2xl bg-zinc-900/85 backdrop-blur-xl border border-white/10 grid place-items-center shadow-xl hover:bg-zinc-800/85 active:scale-95 transition-all"
          title="Report"
          aria-label="Report something"
        >
          <AlertTriangle className="h-5 w-5" style={{ color: "var(--dc-primary)" }}/>
        </button>
        <button
          type="button"
          onClick={() => setWaitTimesOpen(true)}
          data-testid="action-stack-earnings"
          className="h-11 w-11 rounded-2xl bg-zinc-900/85 backdrop-blur-xl border border-white/10 grid place-items-center shadow-xl hover:bg-zinc-800/85 active:scale-95 transition-all"
          title="Wait times"
          aria-label="Wait times"
        >
          <ClockPlus className="h-5 w-5 text-white"/>
        </button>
        <button
          type="button"
          onClick={() => {
            const cycle = { streets: "traffic", traffic: "zones", zones: "streets" };
            const next = cycle[layerMode] || "streets";
            setLayerMode(next);
            toast.message(`Map layer: ${next}`);
          }}
          data-testid="action-stack-layers"
          className="h-11 w-11 rounded-2xl bg-zinc-900/85 backdrop-blur-xl border border-white/10 grid place-items-center shadow-xl hover:bg-zinc-800/85 active:scale-95 transition-all"
          title="Layers"
          aria-label="Toggle map layers"
        >
          <Layers className="h-5 w-5 text-white"/>
        </button>
        <button
          type="button"
          onClick={() => {
            setLocating(true);
            setTimeout(() => { setLocating(false); toast.message(geo ? "Re-centred to your position" : "Waiting for location permission"); }, 900);
          }}
          data-testid="action-stack-locate"
          className="h-11 w-11 rounded-2xl bg-zinc-900/85 backdrop-blur-xl border border-white/10 grid place-items-center shadow-xl hover:bg-zinc-800/85 active:scale-95 transition-all"
          title="Locate me"
          aria-label="Re-center to your position"
        >
          {locating
            ? <Loader2 className="h-5 w-5 text-white animate-spin"/>
            : <LocateFixed className="h-5 w-5 text-white"/>}
        </button>
      </div>

      {/* ── BOTTOM SHEET ─────────────────────────────────────────────── */}
      <div
        className="absolute bottom-0 left-0 right-0 z-20 bg-zinc-950 border-t border-white/10 rounded-t-3xl shadow-[0_-8px_30px_rgba(0,0,0,0.5)] pb-20"
        data-testid="driver-bottom-sheet"
      >
        <div className="p-4 pb-6 space-y-3">
          <div className="h-1 w-10 rounded-full bg-white/20 mx-auto"/>

          {activeRide ? (
            <div data-testid="active-ride-card" className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-emerald-500 grid place-items-center flex-shrink-0">
                  <User2 className="h-4 w-4 text-black"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">
                    On Trip · {(activeRide.status || "").replace(/_/g, " ")}
                  </div>
                  <div className="text-lg font-black truncate">{activeRide.rider_name}</div>
                  {activeRide.customer_type && <CustomerTypeBadge type={activeRide.customer_type} short className="mt-0.5" testid="active-ride-customer-type"/>}
                  {activeRide.group_rider_count ? <span className="mt-0.5 ml-1 inline-block text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/40" data-testid="active-ride-group-riders">Group · {activeRide.group_rider_count} riders</span> : null}
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">Fare</div>
                  <div className="text-lg font-black font-mono tabular-nums">{fmtUsd(activeRide.fare_calculated || 0)}</div>
                </div>
              </div>
              <div className="space-y-2 text-xs">
                {activeRide.pickup_address && (
                  <div className="flex items-start gap-2" data-testid="active-ride-pickup">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--dc-primary)" }}/>
                    <div className="min-w-0">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold">Pickup</div>
                      <div className="truncate text-white">{activeRide.pickup_address}</div>
                    </div>
                  </div>
                )}
                {activeRide.destination_address && (
                  <div className="flex items-start gap-2" data-testid="active-ride-destination">
                    <Flag className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0"/>
                    <div className="min-w-0">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold">Dropoff</div>
                      <div className="truncate text-white">{activeRide.destination_address}</div>
                    </div>
                  </div>
                )}
                {ridePin && (
                  <div className="flex items-center gap-2 mt-2 rounded-lg border p-2"
                       style={{ borderColor: "var(--dc-primary)", background: "rgba(255,210,0,0.10)" }}
                       data-testid="active-ride-pin">
                    <KeyRound className="h-3.5 w-3.5" style={{ color: "var(--dc-primary)" }}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] uppercase tracking-widest font-bold text-zinc-400">Ride PIN</div>
                      <div className="text-xl font-black font-mono tabular-nums" style={{ color: "var(--dc-primary)" }}>{ridePin}</div>
                    </div>
                    <button type="button" onClick={copyPin} className="p-1 rounded hover:bg-white/10" aria-label="Copy PIN">
                      <Copy className="h-3.5 w-3.5 text-zinc-300"/>
                    </button>
                    <button type="button" onClick={rollPin} className="p-1 rounded hover:bg-white/10" aria-label="New PIN">
                      <RefreshCw className="h-3.5 w-3.5 text-zinc-300"/>
                    </button>
                  </div>
                )}
              </div>

              {/* v2.8 · Redeem-at-Pickup free-ride PIN. The rider shows their
                  one-time loyalty code; the driver enters it here to mark the
                  free ride redeemed (comp the fare — this never zeroes it). */}
              <div className="rounded-lg border p-2.5" style={{ borderColor: "rgba(251,191,36,0.5)", background: "rgba(251,191,36,0.06)" }} data-testid="freeride-redeem-driver">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Gift className="h-3.5 w-3.5" style={{ color: "var(--dc-primary)" }}/>
                  <span className="text-[9px] uppercase tracking-widest font-black text-zinc-400">Free-Ride Code (loyalty)</span>
                </div>
                {freeRideDone ? (
                  <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-300" data-testid="freeride-redeem-done-driver">
                    <Check className="h-4 w-4"/> Redeemed for {freeRideDone.rider_name} · comp the fare
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <Input
                        value={freeRidePin}
                        onChange={(e) => setFreeRidePin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        inputMode="numeric"
                        placeholder="6-digit code"
                        className="h-9 bg-zinc-900 border-zinc-700 font-mono tracking-[0.25em] text-center"
                        data-testid="freeride-pin-input-driver"
                      />
                      <Button
                        onClick={redeemFreeRidePin}
                        disabled={freeRideBusy || freeRidePin.length !== 6}
                        className="h-9 font-black text-black hover:brightness-110"
                        style={{ background: "var(--dc-primary)" }}
                        data-testid="freeride-pin-submit-driver"
                      >
                        {freeRideBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Redeem"}
                      </Button>
                    </div>
                    {freeRidePin.length === 6 && freeRideOwner && (
                      freeRideOwner.found ? (
                        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-bold text-emerald-300" data-testid="freeride-pin-owner-driver">
                          <Check className="h-3.5 w-3.5"/> Code belongs to {freeRideOwner.rider_name}
                        </div>
                      ) : (
                        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-bold text-rose-300" data-testid="freeride-pin-owner-none-driver">
                          <X className="h-3.5 w-3.5"/> No active free ride matches this code.
                        </div>
                      )
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                <NavigationActionSheet pickup={pickup} destination={destination}/>
                <Button
                  onClick={() => setMeterOpen(true)}
                  className="flex-1 font-black text-black hover:brightness-110"
                  style={{ background: "var(--dc-primary)" }}
                  data-testid="open-meter-cta"
                >
                  <Gauge className="h-4 w-4 mr-1"/>Open Meter
                </Button>
                {ridePolling && <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-400"/>}
              </div>
              {/* v2.8.2 · Escape hatch for a stuck/old trip that can't be worked. */}
              <button
                type="button"
                onClick={dismissStuckRide}
                className="w-full mt-2 text-[11px] text-red-300/80 hover:text-red-300 underline underline-offset-2"
                data-testid="active-ride-close-stuck"
              >
                Trip stuck or shouldn&apos;t be here? Close &amp; clear it
              </button>
            </div>
          ) : (
            <>
              {/* v2.7.65+E127 · Turbo card only lights when driver is IN a hot zone. */}
              {currentZone && (currentZone.heat === "hot" || currentZone.heat === "warm") && (
                <div className="pt-1" data-testid="turbo-zone-active">
                  <div className="text-xl font-black flex items-center gap-2">
                    <Rocket className="h-5 w-5" style={{ color: "var(--dc-secondary)" }}/>
                    You&apos;re in a Turbo zone
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">
                    {currentZone.label} · {currentZone.riders_waiting} rider{currentZone.riders_waiting === 1 ? "" : "s"} waiting · ~{currentZone.median_wait_min}m median wait
                  </div>
                </div>
              )}
              {!currentZone && !online && (
                <div className="pt-1">
                  <div className="text-xl font-black">Waiting for a Turbo zone</div>
                  <div className="text-xs text-zinc-400 mt-1">Zones light up when your area has riders waiting.</div>
                </div>
              )}

              {!online && (
                <>
                  <Card className="bg-zinc-900 border-white/10 p-3 flex items-center gap-3" data-testid="turbo-priority-card">
                    <div className="h-9 w-9 rounded-lg grid place-items-center"
                         style={{ background: "rgba(255,210,0,0.15)" }}>
                      <Rocket className="h-4 w-4" style={{ color: "var(--dc-primary)" }}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white">Priority Mode</div>
                      <div className="text-[11px] text-zinc-400">More rides when it&apos;s slow</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Available</span>
                  </Card>
                  <Card className="bg-zinc-900 border-white/10 p-3 flex items-center gap-3" data-testid="turbo-silver-card">
                    <div className="h-9 w-9 rounded-lg bg-zinc-700 grid place-items-center">
                      <Award className="h-4 w-4 text-zinc-300"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white">Stay Silver</div>
                      <div className="text-[11px] text-zinc-400">0 / 100 points</div>
                    </div>
                    <span className="text-[10px] text-zinc-500 font-mono">Until 12/31</span>
                  </Card>
                  <Button
                    onClick={() => setWaitTimesOpen(true)}
                    className="w-full h-12 font-black text-white text-sm rounded-2xl hover:brightness-110"
                    style={{ background: "var(--dc-secondary)" }}
                    data-testid="view-issues-cta"
                  >
                    View wait times
                  </Button>
                </>
              )}

              {online && (
                <>
                  <div className="pt-1">
                    <div className="text-xl font-black">Waiting for dispatch</div>
                    <div className="text-xs text-zinc-400 mt-1">
                      Alarm fires the moment a booking lands — you&apos;re all set.
                    </div>
                  </div>
                  <Card className="bg-zinc-900 border-white/10 p-3 flex items-center justify-between gap-2" data-testid="online-meter-cta">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg grid place-items-center"
                           style={{ background: "rgba(255,210,0,0.15)" }}>
                        <Gauge className="h-4 w-4" style={{ color: "var(--dc-primary)" }}/>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-white">Meter ready</div>
                        <div className="text-[11px] text-zinc-400">Weatherford + Statewide rate books loaded</div>
                      </div>
                    </div>
                    <Button
                      onClick={() => setMeterOpen(true)}
                      className="font-black text-black hover:brightness-110"
                      style={{ background: "var(--dc-primary)" }}
                      data-testid="open-meter-cta"
                    >
                      Open
                    </Button>
                  </Card>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── SHEET · REPORT ─────────────────────────────────────────── */}
      <Sheet open={reportOpen} onOpenChange={setReportOpen}>
        <SheetContent side="bottom" className="bg-zinc-950 border-t border-white/10 text-white" data-testid="report-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-white text-xl font-black">Something to report?</SheetTitle>
            <SheetDescription className="text-zinc-400 text-xs">
              Logs locally — no dispatch call fires until Derick approves live routing.
            </SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {REPORT_TILES.map(({ key, label, Icon, tone }) => (
              <button
                key={key}
                type="button"
                onClick={() => submitReport(key, label)}
                data-testid={`report-tile-${key}`}
                className="flex flex-col items-center gap-2 rounded-2xl bg-zinc-900 hover:bg-zinc-800 border border-white/10 py-4 active:scale-95 transition-all"
              >
                <div className={`h-12 w-12 rounded-full grid place-items-center ${
                  tone === "ruby"   ? "bg-pink-500/20"    :
                  tone === "yellow" ? "bg-yellow-500/20"  :
                  tone === "sky"    ? "bg-sky-500/20 text-sky-400" :
                  tone === "red"    ? "bg-red-500/20 text-red-400" :
                                       "bg-zinc-500/20 text-zinc-300"
                }`}
                     style={
                       tone === "ruby"   ? { color: "var(--dc-secondary)" } :
                       tone === "yellow" ? { color: "var(--dc-primary)" }   :
                       undefined
                     }
                >
                  <Icon className="h-5 w-5"/>
                </div>
                <span className="text-xs font-bold">{label}</span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* ── SHEET · MAXIMIZE EARNINGS ─────────────────────────────── */}
      <Sheet open={earnOpen} onOpenChange={setEarnOpen}>
        <SheetContent side="bottom" className="bg-zinc-950 border-t border-white/10 text-white h-[85vh] overflow-y-auto" data-testid="earnings-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-white text-xl font-black">Maximize your earnings</SheetTitle>
          </SheetHeader>
          <div className="mt-2 divide-y divide-white/5">
            {EARN_MENU.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => handleEarnItem(item)}
                data-testid={`earnings-item-${item.key}`}
                className="w-full flex items-center gap-3 py-3 text-left hover:bg-zinc-900 rounded-lg px-2 transition-colors"
              >
                <item.Icon className="h-4 w-4 text-zinc-300 flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">{item.label}</div>
                  <div className="text-[11px] text-zinc-400">{item.hint}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-500"/>
              </button>
            ))}
          </div>
          <div className="mt-5 mb-2 text-xs uppercase tracking-widest font-black text-zinc-500">
            Refuel and refresh
          </div>
          <div className="divide-y divide-white/5">
            {REFRESH_MENU.map(({ key, label, hint, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => { setEarnOpen(false); toast.message(`${label} · coming soon`); }}
                data-testid={`earnings-item-${key}`}
                className="w-full flex items-center gap-3 py-3 text-left hover:bg-zinc-900 rounded-lg px-2 transition-colors"
              >
                <Icon className="h-4 w-4 text-zinc-300 flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">{label}</div>
                  <div className="text-[11px] text-zinc-400">{hint}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-500"/>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* ── SHEET · WAIT TIMES (LIVE) ───────────────────────────────── */}
      <Sheet open={waitTimesOpen} onOpenChange={setWaitTimesOpen}>
        <SheetContent side="bottom" className="bg-zinc-950 border-t border-white/10 text-white h-[85vh] overflow-y-auto" data-testid="wait-times-sheet">
          <SheetHeader className="text-left">
            <div className="flex items-start justify-between gap-3">
              <div>
                <SheetTitle className="text-white text-xl font-black flex items-center gap-2">
                  <ClockPlus className="h-4 w-4" style={{ color: "var(--dc-primary)" }}/>Wait times
                </SheetTitle>
                <SheetDescription className="text-zinc-400 text-xs mt-1">
                  Live dispatch-queue snapshot · refreshes every {WAIT_TIMES_POLL_MS / 1000}s
                </SheetDescription>
              </div>
              <button
                type="button"
                onClick={fetchWaitTimes}
                className="text-[10px] uppercase tracking-widest font-bold text-zinc-300 border border-white/20 rounded-full px-2 py-1 hover:bg-white/5"
                data-testid="wait-times-refresh"
              >
                {wtLoading ? <Loader2 className="h-3 w-3 animate-spin inline"/> : <RefreshCw className="h-3 w-3 inline"/>} Refresh
              </button>
            </div>
          </SheetHeader>
          <div className="mt-4 space-y-2" data-testid="wait-times-list">
            {!waitTimes && wtLoading && (
              <div className="text-center text-xs text-zinc-500 py-8"><Loader2 className="h-4 w-4 animate-spin inline mr-2"/>Loading zone metrics…</div>
            )}
            {waitTimes?.zones?.length === 0 && (
              <div className="text-center text-xs text-zinc-500 py-8">No zones configured yet.</div>
            )}
            {waitTimes?.zones?.map((z) => (
              <div
                key={z.key}
                data-testid={`wait-zone-${z.key}`}
                className={`rounded-xl border p-3 ${
                  z.heat === "hot"  ? "border-red-500 bg-red-500/10" :
                  z.heat === "warm" ? "border-amber-500 bg-amber-500/10" :
                                      "border-white/10 bg-zinc-900"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Circle className={`h-2 w-2 ${
                      z.heat === "hot"  ? "fill-red-400 text-red-400" :
                      z.heat === "warm" ? "fill-amber-400 text-amber-400" :
                                          "fill-zinc-500 text-zinc-500"
                    }`}/>
                    <span className="text-sm font-bold">{z.label}</span>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-black ${
                    z.heat === "hot"  ? "text-red-400" :
                    z.heat === "warm" ? "text-amber-400" :
                                        "text-zinc-500"
                  }`}>{z.heat}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-zinc-400">Riders</div>
                    <div className="text-lg font-black font-mono tabular-nums">{z.riders_waiting}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-zinc-400">Drivers</div>
                    <div className="text-lg font-black font-mono tabular-nums">{z.drivers_nearby}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-zinc-400">Median</div>
                    <div className="text-lg font-black font-mono tabular-nums">{z.median_wait_min}m</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {waitTimes?.generated_at && (
            <div className="mt-4 text-center text-[10px] text-zinc-600">
              Snapshot · {new Date(waitTimes.generated_at).toLocaleTimeString()}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ── SHEET · SAFETY HUB ─────────────────────────────────────── */}
      <Sheet open={safetyOpen} onOpenChange={setSafetyOpen}>
        <SheetContent side="bottom" className="bg-zinc-950 border-t border-white/10 text-white h-[90vh] overflow-y-auto" data-testid="safety-sheet">
          <SheetHeader className="text-left">
            <div className="flex items-center gap-3">
              <div>
                <SheetTitle className="text-white text-2xl font-black">Safety Hub</SheetTitle>
                <SheetDescription className="text-zinc-400 text-xs mt-1">
                  Manage your safety preferences and learn how we help keep you safe in every ride.
                </SheetDescription>
              </div>
              <div className="ml-auto h-10 w-10 rounded-lg bg-sky-600 grid place-items-center flex-shrink-0">
                <ShieldPlus className="h-5 w-5 text-white"/>
              </div>
            </div>
          </SheetHeader>
          <div className="mt-4 flex items-center border-b border-white/10" data-testid="safety-tabs">
            {[
              ["tools",       "Tools"],
              ["safeguards",  "Safeguards"],
              ["education",   "Education"],
            ].map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                onClick={() => setSafetyTab(tab)}
                data-testid={`safety-tab-${tab}`}
                className={`flex-1 py-2 text-xs uppercase tracking-widest font-bold ${
                  safetyTab === tab
                    ? "border-b-2"
                    : "text-zinc-500 border-b-2 border-transparent"
                }`}
                style={safetyTab === tab
                  ? { color: "var(--dc-secondary)", borderBottomColor: "var(--dc-secondary)" }
                  : undefined}
              >
                {label}
              </button>
            ))}
          </div>
          {safetyTab === "tools" && (
            <>
              <div className="mt-5 mb-2 text-sm font-black uppercase tracking-widest text-zinc-300">
                Set your preferences
              </div>
              <div className="divide-y divide-white/5">
                {SAFETY_ITEMS.map((item) => {
                  const isConfigured =
                    item.key === "emergency-contacts" ? safety.emergency_contacts.length > 0 :
                    item.key === "share-loc"          ? !!safety.safety_settings?.share_location :
                    item.key === "record-audio"      ? !!safety.safety_settings?.record_audio_enabled :
                    item.key === "pin-verify"         ? !!ridePin :
                    false;
                  return (
                    <div key={item.key} className="flex items-center gap-3 py-3" data-testid={`safety-item-${item.key}`}>
                      <item.Icon className="h-4 w-4 text-zinc-300 flex-shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold">{item.label}</div>
                        <div className="text-[11px] text-zinc-400 leading-snug">{item.hint}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleSafetyItem(item)}
                        className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${
                          isConfigured
                            ? "border-emerald-400 text-emerald-300 bg-emerald-500/10"
                            : "border-white/30 text-zinc-200 hover:bg-white/5"
                        }`}
                        style={isConfigured ? undefined : { borderColor: "var(--dc-secondary)", color: "var(--dc-secondary)" }}
                        data-testid={`safety-item-${item.key}-cta`}
                      >
                        {isConfigured ? "Configured" : "Set up"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {safetyTab === "safeguards" && (
            <div className="mt-6 space-y-3">
              <Card className="bg-zinc-900 border-white/10 p-4">
                <div className="text-sm font-bold text-white flex items-center gap-2">
                  <ShieldPlus className="h-4 w-4 text-sky-400"/>24/7 Trip monitoring
                </div>
                <div className="text-[11px] text-zinc-400 mt-1">Our safety team can see every active trip.</div>
              </Card>
              <Card className="bg-zinc-900 border-white/10 p-4">
                <div className="text-sm font-bold text-white flex items-center gap-2">
                  <Siren className="h-4 w-4 text-rose-400"/>Silent emergency escalation
                </div>
                <div className="text-[11px] text-zinc-400 mt-1">Route panic pings to dispatch + 911 without alerting the passenger.</div>
              </Card>
            </div>
          )}
          {safetyTab === "education" && (
            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={() => nav("/admin/wiki")}
                className="w-full flex items-center gap-3 rounded-xl bg-zinc-900 border border-white/10 p-3 hover:bg-zinc-800"
                data-testid="safety-education-wiki"
              >
                <div className="h-9 w-9 rounded-lg grid place-items-center"
                     style={{ background: "rgba(224,17,95,0.20)" }}>
                  <Sparkles className="h-4 w-4" style={{ color: "var(--dc-secondary)" }}/>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-bold">Knowledge Center</div>
                  <div className="text-[11px] text-zinc-400">Every article, every runbook, one search.</div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-500"/>
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ── DIALOG · EMERGENCY CONTACTS ─────────────────────────────── */}
      <Dialog open={contactsOpen} onOpenChange={setContactsOpen}>
        <DialogContent className="bg-zinc-950 text-white border-white/10" data-testid="emergency-contacts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneCall className="h-4 w-4" style={{ color: "var(--dc-secondary)" }}/>Emergency Contacts
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              People we contact in case of an emergency during your shift.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="emergency-contacts-list">
            {safety.emergency_contacts.length === 0 && (
              <div className="text-xs italic text-zinc-500">No contacts yet — add your first below.</div>
            )}
            {safety.emergency_contacts.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-lg border border-white/10 p-2" data-testid={`emergency-contact-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{c.name}</div>
                  <div className="text-[11px] text-zinc-400 truncate">{c.phone}{c.relationship ? ` · ${c.relationship}` : ""}</div>
                </div>
                <button type="button" onClick={() => removeContact(c.id)} className="text-zinc-400 hover:text-red-400 p-1"
                        aria-label={`Remove ${c.name}`}
                        data-testid={`emergency-contact-remove-${c.id}`}>
                  <Trash2 className="h-4 w-4"/>
                </button>
              </div>
            ))}
          </div>
          <div className="space-y-2 pt-3 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Add new contact</div>
            <Input placeholder="Name" value={newContact.name}
                   onChange={(e) => setNewContact((n) => ({ ...n, name: e.target.value }))}
                   className="bg-zinc-900 border-white/10" data-testid="new-contact-name"/>
            <Input placeholder="Phone (e.g. +15551234567)" value={newContact.phone}
                   onChange={(e) => setNewContact((n) => ({ ...n, phone: e.target.value }))}
                   className="bg-zinc-900 border-white/10" data-testid="new-contact-phone"/>
            <Input placeholder="Relationship (optional)" value={newContact.relationship}
                   onChange={(e) => setNewContact((n) => ({ ...n, relationship: e.target.value }))}
                   className="bg-zinc-900 border-white/10" data-testid="new-contact-relationship"/>
            <Button
              onClick={addContact}
              disabled={addingContact || !newContact.name || !newContact.phone}
              className="w-full font-black text-white hover:brightness-110"
              style={{ background: "var(--dc-secondary)" }}
              data-testid="new-contact-submit"
            >
              {addingContact ? <Loader2 className="h-4 w-4 animate-spin"/> : "Add Contact"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG · PIN VERIFY ────────────────────────────────────── */}
      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent className="bg-zinc-950 text-white border-white/10" data-testid="pin-verify-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-4 w-4" style={{ color: "var(--dc-primary)" }}/>Ride PIN
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Ask the passenger for this PIN before starting the trip.
            </DialogDescription>
          </DialogHeader>
          <div className="text-center py-6" data-testid="pin-value">
            <div className="text-5xl font-black font-mono tabular-nums tracking-widest" style={{ color: "var(--dc-primary)" }}>
              {ridePin || "----"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
              {activeRide ? "Current ride" : "No active ride yet"}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={rollPin} className="flex-1 border-white/20 hover:bg-zinc-900" data-testid="pin-roll">
              <RefreshCw className="h-4 w-4 mr-1"/>New PIN
            </Button>
            <Button onClick={copyPin} disabled={!ridePin} className="flex-1 font-black text-black hover:brightness-110"
                    style={{ background: "var(--dc-primary)" }} data-testid="pin-copy">
              <Copy className="h-4 w-4 mr-1"/>Copy
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG · AUDIO RECORDING ───────────────────────────────── */}
      <Dialog open={audioOpen} onOpenChange={setAudioOpen}>
        <DialogContent className="bg-zinc-950 text-white border-white/10" data-testid="audio-record-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="h-4 w-4" style={{ color: "var(--dc-secondary)" }}/>Record Ride Audio
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Audio never leaves your device — saved as an encrypted local blob until you export it.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-2">
            <span className="text-xs text-zinc-400">Auto-record on trip start</span>
            <Switch
              checked={!!safety.safety_settings?.record_audio_enabled}
              onCheckedChange={(v) => toggleSafetySetting("record_audio_enabled", v)}
              data-testid="audio-auto-toggle"
            />
          </div>
          <div className="flex flex-col items-center gap-3 py-6">
            <div className={`h-20 w-20 rounded-full grid place-items-center transition-all ${recording ? "animate-pulse" : ""}`}
                 style={{ background: recording ? "var(--dc-danger)" : "var(--dc-secondary)" }}>
              <Mic className="h-8 w-8 text-white"/>
            </div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">
              {recording ? "Recording…" : "Idle"}
            </div>
          </div>
          <div className="flex gap-2">
            {!recording ? (
              <Button onClick={startRecording} className="flex-1 font-black text-white hover:brightness-110"
                      style={{ background: "var(--dc-secondary)" }} data-testid="audio-start">
                Start
              </Button>
            ) : (
              <Button onClick={stopRecording} className="flex-1 font-black text-white hover:brightness-110"
                      style={{ background: "var(--dc-danger)" }} data-testid="audio-stop">
                Stop
              </Button>
            )}
          </div>
          {lastRecordingUrl && (
            <audio controls src={lastRecordingUrl} className="w-full mt-2" data-testid="audio-playback"/>
          )}
        </DialogContent>
      </Dialog>

      {/* ── SHEET · PROFILE ────────────────────────────────────────── */}
      <Sheet open={profileOpen} onOpenChange={setProfileOpen}>
        <SheetContent side="left" className="bg-zinc-950 border-r border-white/10 text-white w-[85vw] sm:w-[420px] overflow-y-auto" data-testid="profile-sheet">
          <div className="flex items-center justify-between mb-6">
            <button
              type="button"
              onClick={() => setProfileOpen(false)}
              className="h-10 w-10 grid place-items-center rounded-xl hover:bg-zinc-800"
              aria-label="Close menu"
              data-testid="profile-sheet-close"
            >
              <X className="h-5 w-5"/>
            </button>
          </div>
          <div className="flex items-start gap-4 mb-6">
            <div className="h-16 w-16 rounded-full border-2 bg-zinc-800 grid place-items-center overflow-hidden flex-shrink-0"
                 style={{ borderColor: "var(--dc-secondary)" }}>
              {me?.avatar_url
                ? <img src={me.avatar_url} alt={me?.name || "Driver"} className="h-full w-full object-cover"/>
                : <User2 className="h-8 w-8 text-zinc-400"/>}
            </div>
            <div className="min-w-0">
              <div className="text-lg font-black truncate">{me?.name || me?.email || "Driver"}</div>
              <div className="text-[11px] text-zinc-400 truncate">
                {[me?.vehicle_make, me?.vehicle_model, me?.taxi_number ? `#${me.taxi_number}` : null].filter(Boolean).join(" · ") || "Vehicle unassigned"}
              </div>
              <span className="inline-block mt-1 text-[10px] uppercase tracking-widest font-black text-zinc-300 bg-zinc-700 rounded-full px-2 py-0.5">
                Silver
              </span>
            </div>
          </div>
          <Card className="bg-zinc-900 border-white/10 p-4 mb-6" data-testid="profile-stats">
            <div className="grid grid-cols-3 divide-x divide-white/10 text-center">
              <div>
                <div className="text-lg font-black">{earnings?.total_count ?? "—"}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">Rides</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-lg font-black flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 fill-current" style={{ color: "var(--dc-primary)" }}/>
                  {me?.rating_avg ? me.rating_avg.toFixed(1) : "5.0"}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">Rating</div>
              </div>
              <div>
                <div className="text-lg font-black">{me?.years_active ?? "—"}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">Years</div>
              </div>
            </div>
          </Card>

          <ProfileGroup label="More Ways to Earn">
            <ProfileRow Icon={Rocket}       label="Boost hours"    onClick={() => toast.message("Coming soon")}/>
            <ProfileRow Icon={CalendarDays} label="Scheduled rides" onClick={() => toast.message("Coming soon")}/>
          </ProfileGroup>
          <ProfileGroup label="Vehicle and Devices">
            <ProfileRow Icon={Car}       label="My Vehicle"    onClick={() => nav("/verify/driver/me")}/>
            <ProfileRow Icon={Gauge}     label="Meter Presets" onClick={() => nav("/meter")}/>
          </ProfileGroup>
          <ProfileGroup label="Account">
            <ProfileRow Icon={User} label="Ride as myself" onClick={openRiderMode} testId="menu-ride-as-myself"/>
            <ProfileRow Icon={Home}      label="Classic Dashboard" onClick={() => nav("/dashboard")}/>
            <ProfileRow Icon={User2}     label="Profile & Docs"    onClick={() => nav("/dashboard")}/>
          </ProfileGroup>
          <ProfileGroup label="Support and Resources" defaultOpen>
            <ProfileRow Icon={ShieldPlus} label="Safety Hub"      onClick={() => { setProfileOpen(false); setSafetyOpen(true); }} testId="menu-open-safety"/>
            <ProfileRow Icon={Sparkles}   label="Knowledge Center" onClick={() => nav("/admin/wiki")}/>
            <ProfileRow Icon={PhoneCall}  label="Call Dispatch"    onClick={() => { window.location.href = `tel:${DISPATCH_TEL}`; }} testId="menu-call-dispatch"/>
          </ProfileGroup>
        </SheetContent>
      </Sheet>

      {/* ── SHEET · METER ──────────────────────────────────────────── */}
      <Sheet open={meterOpen} onOpenChange={setMeterOpen}>
        <SheetContent side="bottom" className="bg-zinc-950 border-t border-white/10 text-white h-[75vh]" data-testid="meter-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-white flex items-center gap-2">
              <Gauge className="h-4 w-4" style={{ color: "var(--dc-primary)" }}/>Taxi Meter
            </SheetTitle>
            <SheetDescription className="text-zinc-400 text-xs">
              Existing meter engine runs in its own full-page mode. Pick a rate book below.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button
              onClick={() => nav("/meter")}
              className="h-14 font-black text-black text-sm hover:brightness-110"
              style={{ background: "var(--dc-primary)" }}
              data-testid="jump-meter-weatherford"
            >
              <Car className="h-4 w-4 mr-2"/>Weatherford
            </Button>
            <Button
              onClick={() => nav("/meter-statewide")}
              variant="outline"
              className="h-14 font-black border-white/20 hover:bg-zinc-800"
              data-testid="jump-meter-statewide"
            >
              <Car className="h-4 w-4 mr-2"/>Statewide
            </Button>
          </div>
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest font-black text-zinc-500 mb-2">Preferences</div>
            <DriverPreferencesCard/>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ProfileGroup({ label, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/5" data-testid={`profile-group-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-3"
      >
        <span className="text-sm font-bold">{label}</span>
        <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && <div className="pb-3 space-y-1">{children}</div>}
    </div>
  );
}
function ProfileRow({ Icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-zinc-800 text-left"
      data-testid={testId}
    >
      <Icon className="h-4 w-4 text-zinc-300"/>
      <span className="flex-1 text-sm">{label}</span>
      <ChevronRight className="h-4 w-4 text-zinc-500"/>
    </button>
  );
}
